import { ApiResponse, FormStore, GlobalStore } from '@roc/feature-app-core';
import { action, flow, makeObservable, observable } from 'mobx';
import { ReferralsService } from '../services';
import { getMultiPageScreenshotGridWaitingForImages } from '@roc/ui';
import { parseAddress } from '@roc/feature-utils';

const form = {
  fields: {
    bankName: {
      value: '',
      error: null,
      rule: 'required',
      attribute: 'bank name',
    },
    accountName: {
      value: '',
      error: null,
      rule: 'required',
      attribute: 'bank account name',
    },
    routingNumber: {
      value: '',
      error: null,
      rule: 'required|regex:/^\\d{9}$/|alpha_dash',
      message: 'Routing Number must be 9 digits',
    },
    accountNumber: {
      value: '',
      error: null,
      rule: 'required|regex:/^\\d+$/',
      attribute: 'bank account number',
    },
    accountNumberConfirmation: {
      value: '',
      error: null,
      rule: 'required|same:accountNumber|regex:/^\\d+$/',
      message: 'This field must match the bank account number.',
    },
    address: {
      value: '',
      error: null,
      rule: 'required',
      message: 'The address is required.',
    },
    __isAddressInCorrectFormat__: {
      value: true,
      error: null,
      rule: 'accepted',
      message: 'Invalid Address Format.',
    },
    streetNumber: {
      value: '',
      error: null,
      rule: '',
    },
    streetName: {
      value: '',
      error: null,
      rule: 'required',
      message: 'The Street Name is required.',
    },
    city: {
      value: '',
      error: null,
      rule: 'required',
      message: 'The City is required.',
    },
    state: {
      value: '',
      error: null,
      rule: 'required',
      message: 'The State is required.',
    },
    county: {
      value: '',
      error: null,
      rule: '',
    },
    zipCode: {
      value: '',
      error: null,
      rule: 'required',
      message: 'The Zip Code is required.',
    },
    latitude: {
      value: 0,
      error: null,
      rule: '',
    },
    longitude: {
      value: 0,
      error: null,
      rule: '',
    },
    aptNumber: {
      value: '',
      error: null,
      rule: '',
    },
  },
  meta: {
    isValid: false,
    error: null,
  },
};

export class ReferralWiringInformationStore extends FormStore {
  private globalStore: GlobalStore;
  private referralService: ReferralsService;

  activeStep: number;
  wiringInformationOpen: boolean;
  wiringInformation: any;
  isSigned: boolean;
  edit: boolean;
  screenshotElementIds: string[];
  isPdfPreviewModalOpen: boolean;
  pdfTitle: string;
  pdfData: any;

  constructor(globalStore: GlobalStore) {
    super(form, globalStore);
    this.globalStore = globalStore;
    this.referralService = new ReferralsService();
    this.screenshotElementIds = ['information'];
    makeObservable(this, {
      activeStep: observable,
      isSigned: observable,
      isPdfPreviewModalOpen: observable,
      pdfTitle: observable,
      pdfData: observable,
      wiringInformationOpen: observable,
      wiringInformation: observable,
      edit: observable,
      moveToSignatureStep: action,
      showScreenshotGrid: action,
      hideScreenshotGrid: action,
      loadWiringInformationFromMemory: action,
      getReferralWiringInformation: flow,
      sign: flow,
      downloadSignedWiringInformation: flow,
    });
    this.activeStep = 0;
    this.edit = false;
    this.wiringInformation = null;
  }

  loadWiringInformationFromMemory() {
    if (this.wiringInformation) {
      this.form.fields.bankName.value = this.wiringInformation.bankName;
      this.form.fields.accountName.value = this.wiringInformation.accountName;
      this.form.fields.accountNumber.value = this.wiringInformation.accountNumber;
      this.form.fields.accountNumberConfirmation.value = this.wiringInformation.accountNumber;
      this.form.fields.routingNumber.value = this.wiringInformation.routingNumber;
      this.form.fields.address.value = this.wiringInformation.address;
      this.form.fields.city.value = this.wiringInformation.city;
      this.form.fields.state.value = this.wiringInformation.state;
      this.form.fields.streetName.value = this.wiringInformation.streetName;
      this.form.fields.streetNumber.value = this.wiringInformation.streetNumber;
      this.form.fields.zipCode.value = this.wiringInformation.zipCode;
    }
  }

  moveToSignatureStep() {
    this.runFormValidationWithMessage();
    if (this.form.meta.isValid) {
      this.activeStep = 1;
    }
  }

    saveAddressField(address: string, addressDetails, geometryDetails) {
    const parsedAddress = parseAddress(
      address,
      addressDetails,
      geometryDetails
    );
    this.onFieldChange('address', address);
    this.onFieldChange('streetNumber', parsedAddress.street_number);
    this.onFieldChange('streetName', parsedAddress.street);
    this.onFieldChange('city', parsedAddress.city);
    this.onFieldChange('zipCode', parsedAddress.zip);
    this.onFieldChange('state', parsedAddress.state);
    this.onFieldChange('latitude', parsedAddress.latitude);
    this.onFieldChange('longitude', parsedAddress.longitude);
  }

  *sign(userId: number, signature: string) {
    this.showScreenshotGrid();
    const formDataWithPdfAttached: FormData = yield getMultiPageScreenshotGridWaitingForImages(
      'ReferralWiringInformation',
      this.screenshotElementIds,
      []
    );
    this.hideScreenshotGrid();

    const data = {
      signature: signature,
      userAgent: navigator.userAgent,
      userId: userId,
      referralWiringInformation: this.getFormValues(),
    };

    formDataWithPdfAttached.append(
      'data',
      new Blob([JSON.stringify(data)], { type: 'application/json' })
    );
    const response: ApiResponse = yield this.referralService.sign(
      formDataWithPdfAttached
    );
    //TODO if resonse is ok
    this.isSigned = true;
    this.edit = false;
    this.globalStore.notificationStore.showSuccessNotification({
      message: 'Your signature was saved successfully.',
    });
  }

  *downloadSignedWiringInformation(userId: number) {
    try {
      const blob = yield this.referralService.downloadWiringInfo(userId);
      const reader = new FileReader();
      reader.readAsArrayBuffer(blob);
      reader.onloadend = () => {
        this.pdfTitle = 'Referral Information';
        this.pdfData = reader.result; // The ArrayBuffer
        this.isPdfPreviewModalOpen = true;
      };
    } catch (e) {
      console.error(e);
      this.globalStore.notificationStore.showErrorNotification({
        message: 'We had an issue while downloading the document.',
      });
    }
  }

  *getReferralWiringInformation(userId) {
    try {
      const response = yield this.referralService.getReferralWiringInformation(
        userId
      );
      // TODO check response status
      this.isSigned = !!response?.data?.data?.signatureId;
      this.wiringInformation = response.data?.data;
      this.loadWiringInformationFromMemory();
    } catch (error) {
      console.log('Error', error);
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Error while getting the referral wiring information',
      });
    }
  }

  showScreenshotGrid() {
    const parentTag = document.getElementById('screenshotParent');
    if (parentTag) {
      parentTag.style.position = 'absolute';
      parentTag.style.top = '0';
      parentTag.style.zIndex = '-9999';
      parentTag.style.display = 'block';
      parentTag.style.width = '841px';
    }
  }

  hideScreenshotGrid() {
    const parentTag = document.getElementById('screenshotParent');
    if (parentTag) {
      parentTag.style.display = 'none';
    }
  }
}
